import { useState, useMemo, useEffect } from 'react';
import { Deployment, SortField, SortConfig, FilterConfig, ViewState } from '../types';
import { formatDate, getEnvColor, getInitialViewState, saveToCookie, updateUrl, loadFavorites, saveFavorites } from '../utils';
import healthyIcon from '../assets/icons/health-healthy.svg';
import unhealthyIcon from '../assets/icons/health-unhealthy.svg';
import degradedIcon from '../assets/icons/health-degraded.svg';
import unknownIcon from '../assets/icons/health-unknown.svg';
import statusSuccessIcon from '../assets/icons/status-success.svg';
import statusFailedIcon from '../assets/icons/status-failed.svg';
import statusRunningIcon from '../assets/icons/status-running.svg';
import statusPendingIcon from '../assets/icons/status-pending.svg';
import statusUnknownIcon from '../assets/icons/status-unknown.svg';

const healthIcons: Record<string, string> = {
  healthy: healthyIcon,
  unhealthy: unhealthyIcon,
  degraded: degradedIcon,
  unknown: unknownIcon,
};

const statusIcons: Record<string, string> = {
  success: statusSuccessIcon,
  failed: statusFailedIcon,
  running: statusRunningIcon,
  pending: statusPendingIcon,
  unknown: statusUnknownIcon,
};

// Map raw API statuses to normalized display values
const deploymentStatusMap: Record<string, string> = {
  // Pending states
  pending: 'pending',
  created: 'pending',
  queued: 'pending',
  ready: 'pending',
  // Running states
  in_progress: 'running',
  inprogress: 'running',
  start: 'running',
  running: 'running',
  // Failed states
  stop: 'failed',
  stopped: 'failed',
  failed: 'failed',
  failure: 'failed',
  // Success state
  success: 'success',
};

// Normalize raw API status to display status
const normalizeDeploymentStatus = (status: string | undefined): string => {
  if (!status) return 'unknown';
  const normalized = status.toLowerCase().replace(/[_-]/g, '');
  return deploymentStatusMap[normalized] || deploymentStatusMap[status.toLowerCase()] || 'unknown';
};

interface DeploymentTableProps {
  deployments: Deployment[];
  loading: boolean;
  error: string | null;
  onRowClick?: (deployment: Deployment) => void;
}

export function DeploymentTable({
  deployments,
  loading,
  error,
  onRowClick,
}: DeploymentTableProps) {
  // Initialize state from URL params > cookies > defaults
  const initialState = getInitialViewState();

  const [sortConfig, setSortConfig] = useState<SortConfig>(initialState.sort);
  const [filters, setFilters] = useState<FilterConfig>(initialState.filters);
  const [showDummy, setShowDummy] = useState(initialState.showDummy);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(initialState.showFavoritesOnly);
  const [favorites, setFavorites] = useState<Set<string>>(() => loadFavorites());

  // Sync state to URL and cookie when it changes
  useEffect(() => {
    const state: ViewState = { filters, sort: sortConfig, showDummy, showFavoritesOnly };
    updateUrl(state);
    saveToCookie(state);
  }, [filters, sortConfig, showDummy, showFavoritesOnly]);

  // Get unique environments for filter dropdown
  const environments = useMemo(() => {
    const envs = new Set(deployments.map((d) => d.Env));
    return Array.from(envs).sort();
  }, [deployments]);

  // Get unique applications for filter dropdown (filtered by showDummy)
  const applications = useMemo(() => {
    let filtered = deployments;
    if (!showDummy) {
      filtered = deployments.filter((d) => !d.App.toLowerCase().includes('dummy'));
    }
    const apps = new Set(filtered.map((d) => d.App));
    return Array.from(apps).sort();
  }, [deployments, showDummy]);

  // Filter and sort deployments
  const filteredDeployments = useMemo(() => {
    let result = [...deployments];

    // Filter out Dummy apps unless showDummy is checked
    if (!showDummy) {
      result = result.filter((d) => !d.App.toLowerCase().includes('dummy'));
    }

    // Apply favorites filter
    if (showFavoritesOnly) {
      result = result.filter((d) => favorites.has(`${d.Env}#${d.App}`));
    }

    // Apply filters
    if (filters.env) {
      result = result.filter((d) => d.Env === filters.env);
    }
    if (filters.app) {
      result = result.filter((d) => d.App === filters.app);
    }

    // Apply sorting
    result.sort((a, b) => {
      const aVal = a[sortConfig.field] || '';
      const bVal = b[sortConfig.field] || '';

      if (aVal < bVal) return sortConfig.order === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.order === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [deployments, filters, sortConfig, showDummy, showFavoritesOnly, favorites]);

  const handleSort = (field: SortField) => {
    setSortConfig((prev) => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIndicator = (field: SortField) => {
    if (sortConfig.field !== field) return null;
    return sortConfig.order === 'asc' ? ' ▲' : ' ▼';
  };

  const toggleFavorite = (env: string, app: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    const key = `${env}#${app}`;
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      saveFavorites(next);
      return next;
    });
  };

  if (loading) {
    return <div className="loading">Loading deployments...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="deployment-table-container">
      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label htmlFor="env-filter">Environment:</label>
          <select
            id="env-filter"
            value={filters.env}
            onChange={(e) => setFilters((f) => ({ ...f, env: e.target.value }))}
          >
            <option value="">All Environments</option>
            {environments.map((env) => (
              <option key={env} value={env}>
                {env}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="app-filter">Application:</label>
          <select
            id="app-filter"
            value={filters.app}
            onChange={(e) => setFilters((f) => ({ ...f, app: e.target.value }))}
          >
            <option value="">All Applications</option>
            {applications.map((app) => (
              <option key={app} value={app}>
                {app}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group checkbox-group">
          <label htmlFor="show-favorites-only">
            <input
              id="show-favorites-only"
              type="checkbox"
              checked={showFavoritesOnly}
              onChange={(e) => setShowFavoritesOnly(e.target.checked)}
            />
            Favorites Only
          </label>
        </div>
        <div className="filter-group checkbox-group">
          <label htmlFor="show-dummy">
            <input
              id="show-dummy"
              type="checkbox"
              checked={showDummy}
              onChange={(e) => setShowDummy(e.target.checked)}
            />
            Show Dummy
          </label>
        </div>
        <div className="filter-info">
          Showing {filteredDeployments.length} of {deployments.length} deployments
        </div>
      </div>

      {/* Table */}
      <table className="deployment-table">
        <thead>
          <tr>
            <th className="favorite-col"></th>
            <th onClick={() => handleSort('Account')} className="sortable">
              Account{getSortIndicator('Account')}
            </th>
            <th onClick={() => handleSort('Env')} className="sortable">
              Environment{getSortIndicator('Env')}
            </th>
            <th onClick={() => handleSort('App')} className="sortable">
              Application{getSortIndicator('App')}
            </th>
            <th onClick={() => handleSort('Ver')} className="sortable">
              Version{getSortIndicator('Ver')}
            </th>
            <th onClick={() => handleSort('DeployAt')} className="sortable">
              Deployed At{getSortIndicator('DeployAt')}
            </th>
            <th onClick={() => handleSort('DeployFor')} className="sortable">
              Deployed By{getSortIndicator('DeployFor')}
            </th>
            <th className="status-col">Status</th>
            <th className="health-col">Health</th>
          </tr>
        </thead>
        <tbody>
          {filteredDeployments.length === 0 ? (
            <tr>
              <td colSpan={9} className="no-data">
                No deployments found
              </td>
            </tr>
          ) : (
            filteredDeployments.map((deployment, index) => {
              const isFavorite = favorites.has(`${deployment.Env}#${deployment.App}`);
              return (
              <tr
                key={`${deployment.Env}-${deployment.App}-${index}`}
                className={`env-${deployment.Env.toLowerCase()}`}
                style={{ backgroundColor: getEnvColor(deployment.Env) }}
                onClick={() => onRowClick?.(deployment)}
              >
                <td
                  className="favorite-cell"
                  onClick={(e) => toggleFavorite(deployment.Env, deployment.App, e)}
                  style={{ cursor: 'pointer', color: isFavorite ? '#FFD700' : '#ccc' }}
                >
                  {isFavorite ? '★' : '☆'}
                </td>
                <td className="account-cell">{deployment.Account || ''}</td>
                <td className="env-cell">{deployment.Env}</td>
                <td>{deployment.App}</td>
                <td className="version-cell">{deployment.Ver}</td>
                <td>{formatDate(deployment.DeployAt)}</td>
                <td>{deployment.DeployFor}</td>
                <td className={`status-cell status-${normalizeDeploymentStatus(deployment.DeploymentStatus)}`}>
                  <img
                    src={statusIcons[normalizeDeploymentStatus(deployment.DeploymentStatus)]}
                    alt={deployment.DeploymentStatus || 'Unknown'}
                    title={deployment.DeploymentStatus || 'Unknown'}
                    className="status-icon"
                  />
                </td>
                <td className={`health-cell health-${(deployment.HealthStatus || 'unknown').toLowerCase()}`}>
                  <img
                    src={healthIcons[(deployment.HealthStatus || 'unknown').toLowerCase()] || healthIcons.unknown}
                    alt={deployment.HealthStatus || 'Unknown'}
                    title={deployment.HealthStatus || 'Unknown'}
                    className="health-icon"
                  />
                </td>
              </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
